import { PropertyColumn } from "@/enums/tables/propertyColumn.enum";
import { AddressHelper } from "@/helpers/addressHelper";
import { MatHelper } from "@/helpers/mat-helper.helper";
import { PropertyAttributeHelper } from "@/helpers/property-attribute.helper";
import { PropertyHelper } from "@/helpers/property.helper";
import { UserHelper } from "@/helpers/user.helper";
import { AlertService } from "@/services/alert.service";
import { AppService } from "@/services/app.service";
import { SelectionModel } from "@angular/cdk/collections";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { RouterLink } from "@angular/router";
import {
  PropertiesService,
  PropertyAccessTypeFilter,
  PropertyAttribute,
  PropertyChildrenTypeFilter,
  PropertyType,
  RealProperty,
  SellingStatus,
  SortDirection,
  UsersService,
} from "@eemo/api-client";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { forkJoin, Subscription } from "rxjs";
import { DetailRowDirective } from "../../directives/tables/table-detail-row/detail-row.directive";
import { ConfirmDialogComponent } from "../confirm-dialog/confirm-dialog.component";
import { DataLoadingComponent } from "../data-loading/data-loading.component";
import { FilterPropertyListData } from "../filters-property-list/filters-property-list.component";
import { ImgDisplayComponent } from "../img-display/img-display.component";
import { MoreDetailsComponent } from "../more-details/more-details.component";
import { PropertyAddSimpleDialogComponent } from "../property-add-simple-dialog/property-add-simple-dialog.component";
import { PropertyCertifyDialogComponent } from "../property-certify-dialog/property-certify-dialog.component";
import { PropertyCloneDialogComponent } from "../property-clone-dialog/property-clone-dialog.component";
import { PropertyDetailsComponent } from "../property-details/property-details.component";
import { PropertyTransfertDialogComponent } from "../property-transfert-dialog/property-transfert-dialog.component";
import { ShareDialogComponent } from "../share-dialog/share-dialog.component";

import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import { ObjectUpdatedType } from "@/enums/ObjectUpdatedType.enum";
import { DateFormatPipe } from "@/extensions/datePipe";
import { ActionService } from "@/services/action.service";
import { AppConfigService } from "@/services/app-config.service";
import { CurrencyPipe, NgIf } from "@angular/common";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSort, MatSortModule, Sort } from "@angular/material/sort";
import { TableConfigDirective } from "../../directives/tables/table-config/table-config.directive";
import { CertificationBadgeComponent } from "../certification-badge/certification-badge.component";

export interface IRealProperty extends RealProperty {
  photo?: File;
  floor?: string;
  num?: string;
  hasParent?: boolean;
  typeName?: string;
  typeIcon?: string;
  isOnSelling?: boolean;
  saleStatusDisplay?: string;
  sellingPrice?: number;
  ownerDisplay?: string;
  certificationDisplay?: string;
  city?: string;
  isMyProperty?: boolean;
  isSharedProperty?: boolean;
  routerLink?: string[];
  canSelect?: boolean;
  canUpdate?: boolean;
  canAddShare?: boolean;
  canTransfert?: boolean;
  canCertify?: boolean;
  canDelete?: boolean;
  canArchive?: boolean;
  alreadyBeenLoaded?: boolean;
  isRow?: boolean;
  sharedByDisplay?: string;
  rowActionId?: string;
  rowLoading?: boolean;
  isFavorite?: boolean;
}

export interface DataRequestFilter {
  name?: string;
  parentId?: string;
  types?: PropertyType[];
  attributeCodes?: string[];
  attributeValues?: string[];
  childrenType?: PropertyChildrenTypeFilter;
}

type sortFieldType =
  | "name"
  | "type"
  | "eemoId"
  | "city"
  | "certificationDate"
  | "dateCreate"
  | "dateUpdate"
  | "sellingPrice";

@Component({
  selector: "app-property-list",
  templateUrl: "./property-list.component.html",
  styleUrls: ["./property-list.component.scss"],
  animations: MatHelper.getTableAnimations(),
  standalone: true,
  imports: [
    CurrencyPipe,
    NgIf,
    MatTableModule,
    MatSortModule,
    TableConfigDirective,
    MatCheckboxModule,
    TooltipDirective,
    CertificationBadgeComponent,
    MatIconModule,
    ImgDisplayComponent,
    RouterLink,
    MatButtonModule,
    MatMenuModule,
    PropertyDetailsComponent,
    MoreDetailsComponent,
    DetailRowDirective,
    MatPaginatorModule,
    DataLoadingComponent,
    TranslateModule,
    MatProgressSpinnerModule,
    DateFormatPipe,
  ],
})
export class PropertyListComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild(MatSort) sort!: MatSort;

  readonly PAGING_ITEMS = 12;

  @Input() propParentId = null;
  @Input() filterData: FilterPropertyListData;
  @Input() displayFullColumns = false;
  @Input() archiveMode = false;
  @Input() reload = 0;
  @Input() lightColumns: PropertyColumn[] = [];
  @Input() fullColumns: PropertyColumn[] = [];
  @Input() defaultChildrenType = PropertyChildrenTypeFilter.BOUND;
  @Output() selectedProps = new EventEmitter<IRealProperty[]>();
  @Output() newList = new EventEmitter<IRealProperty[]>();
  @Input() condoMode = false;
  @Input() selectionEnabled = true;
  @Input() selectionMustBeCertified = false;
  @Input() showShareByUser = false;

  subs: Subscription[] = [];
  dataSource: MatTableDataSource<IRealProperty>;
  isLoading = false;
  expandedElement: IRealProperty | null;
  canTransfert = false;
  canCertify = false;
  selection = new SelectionModel<IRealProperty>(true, []);
  COLUMNS_ID = PropertyColumn;
  displayedColumns = [];
  noDataText = "Aucun bien n'est configuré pour le moment";

  favoriteList: RealProperty[] = [];
  myList: RealProperty[] = [];
  sharedList: RealProperty[] = [];

  loadingMoreFavorite = false;
  loadingMoreMy = false;
  loadingMoreShared = false;

  hasMoreFavorite = false;
  hasMoreMy = false;
  hasMoreShared = false;

  countMoreFavorite = 0;
  countMoreMy = 0;
  countMoreShared = 0;

  // FILTERS
  propertyTypeListTypes = [];
  attributeCodes = [];
  attributeValues = [];

  sortFieldName: sortFieldType;
  sortDirection: SortDirection;

  constructor(
    private propertyService: PropertiesService,
    private propHelper: PropertyHelper,
    private dialog: MatDialog,
    private alertService: AlertService,
    private appService: AppService,
    private userHelper: UserHelper,
    private userService: UsersService,
    private translateService: TranslateService,
    public actionService: ActionService,
    private appConfigService: AppConfigService
  ) {}

  ngOnInit(): void {
    if (this.archiveMode) {
      this.noDataText = "Aucun bien n'a été archivé";
    }
    this.displayedColumns = this.lightColumns;
    this.selection.compareWith = (o1, o2) => o1.uid === o2.uid;
    this.loadData();

    this.subs.push(
      this.appConfigService.objectUpdated.subscribe((type) => {
        if (type === ObjectUpdatedType.PROPERTY) {
          this.loadData();
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.propParentId && !changes.propParentId?.firstChange) ||
      !changes.filterData?.firstChange ||
      !changes.reload?.firstChange
    ) {
      this.unSelectAll();
      this.loadData();
    }
    if (!changes.displayFullColumns?.firstChange) {
      this.displayColumnHandler();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  onSortChange(sort: Sort) {
    const sortField = sort.active; // e.g. 'name'
    const sortDirection = sort.direction; // 'asc' | 'desc' | ''

    if (sortDirection !== "") {
      this.sortFieldName = sortField as sortFieldType;
      this.sortDirection =
        sortDirection === "desc" ? SortDirection.DESC : SortDirection.ASC;
    } else {
      this.sortFieldName = undefined;
      this.sortDirection = undefined;
    }

    this.loadData();
  }

  displayColumnHandler() {
    if (this.displayFullColumns) {
      this.displayedColumns = this.fullColumns;
    } else {
      this.displayedColumns = this.lightColumns;
    }
  }

  sendItemSelected() {
    this.selectedProps.next(this.selection.selected);
  }

  afterGetLoadData() {
    // parent/child must be redesigned
    /*for (let curList of [this.favoriteList, this.myList, this.sharedList]) {
      const list: IRealProperty[] = curList;
      // START ---------------------- code for sort parent and child cause backend doesn't.
      // sort the list by parent/child hierarchy
      // 1st level elements
      const sortedDataList: IRealProperty[] = list.filter(
        (element) => element?.parent?.uid === this.propParentId
      );
      // Other levels
      list
        .filter((element) => element?.parent?.uid !== this.propParentId)
        .forEach((element) => {
          const index = sortedDataList.findIndex(
            (d) => d.uid === element?.parent?.uid
          );
          element.hasParent = index > -1;
          sortedDataList.splice(index + 1, 0, element);
        });
      curList = sortedDataList;
    }*/

    let dataSourceList = [];
    if (this.favoriteList.length > 0) {
      dataSourceList = dataSourceList.concat(
        [this.getSeparatorRow("favoriteProperty")],
        this.favoriteList,
        this.hasMoreFavorite ? this.getMoreRow("favoriteProperty") : []
      );
    }
    if (this.myList.length > 0) {
      dataSourceList = dataSourceList.concat(
        [this.getSeparatorRow("myProperty")],
        this.myList,
        this.hasMoreMy ? this.getMoreRow("myProperty") : []
      );
    }
    if (this.sharedList.length > 0) {
      dataSourceList = dataSourceList.concat(
        [this.getSeparatorRow("sharedProperty")],
        this.sharedList,
        this.hasMoreShared ? this.getMoreRow("sharedProperty") : []
      );
    }

    this.dataSource = new MatTableDataSource(dataSourceList);
    this.dataSource.data.forEach((d) => {
      d.isOnSelling =
        !d.demo && d.onGoingSelling?.status === SellingStatus.SELLING;
      d.sellingPrice = d.onGoingSelling?.price;
      d.saleStatusDisplay = d.isOnSelling
        ? this.translateService.instant(
            "enum.SellingStatus." + d.onGoingSelling.status
          )
        : "Non";
      d.isFavorite =
        this.favoriteList.find((p) => p.uid === d.uid) !== undefined;
      d.typeName = this.translateService.instant("enum.PropertyType." + d.type);
      (d.typeIcon = this.propHelper.getBadgeImageName(d.type)),
        (d.ownerDisplay = UserHelper.userSummmaryDisplayName(d.owner));
      d.certificationDisplay = d.certificationType || "NONE";
      d.city = AddressHelper.getCity(d.address);
      if (this.userHelper.isPropertyOwner(d)) {
        d.isMyProperty = true;
        d.isSharedProperty = false;
      } else {
        d.isMyProperty = false;
        d.isSharedProperty = true;
      }
      d.sharedByDisplay = d.sharedBy
        ? UserHelper.userSummmaryDisplayName(d.sharedBy)
        : "";
      /*if (d.uid) {
        this.getAttrProperties(d.uid);
      }*/
      d.routerLink = this.propHelper.getPropRouterLink(d);
      d.canSelect = this.selectionMustBeCertified
        ? d.certified && this.userHelper.canUpdateProperty(d)
        : this.userHelper.canUpdateProperty(d);
      d.canUpdate = this.userHelper.canUpdateProperty(d);
      d.canAddShare =
        d.certified && !d.archived && this.userHelper.canUpdateProperty(d);
      d.canCertify = !d.archived && this.userHelper.canUpdateProperty(d);
      d.canTransfert = !d.archived && this.userHelper.canTransfertProperty(d);
      d.canCertify = !d.archived && this.userHelper.canCertifyProperty(d);
      d.canDelete = this.userHelper.canDeleteProperty(d);
      d.canArchive = this.userHelper.canArchiveProperty(d);
    });
    this.newList.next([
      ...this.favoriteList,
      ...this.myList,
      ...this.sharedList,
    ]);
  }

  afterGetPropAttr(propUid: string, propAttrs: PropertyAttribute[]) {
    const floor: string | null = PropertyAttributeHelper.getFloor(propAttrs);
    const num: string | null = PropertyAttributeHelper.getNum(propAttrs);
    const found = this.dataSource.data.find((d) => d.uid === propUid);
    if (found) {
      found.floor = floor;
      found.num = num;
    }
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.unSelectAll();
      return;
    }

    this.selection.select(...this.dataSource.data.filter((d) => d.canSelect));
    this.sendItemSelected();
  }

  unSelectAll() {
    this.selection.clear();
    this.sendItemSelected();
  }

  onChangeCheckbox(event: any, row: IRealProperty) {
    if (event) {
      this.selection.toggle(row);
      this.sendItemSelected();
    }
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows =
      this.dataSource?.data.filter((d) => d.canSelect).length || 0;
    return numSelected === numRows;
  }

  checkboxLabel(row?: IRealProperty): string {
    if (!row) {
      return `${this.isAllSelected() ? "deselect" : "select"} all`;
    }
    return `${this.selection.isSelected(row) ? "deselect" : "select"} row ${
      row.name
    }`;
  }

  onClickViewdetails(row: IRealProperty) {
    this.expandedElement = this.expandedElement === row ? null : row;
    this.loadPropertyDetails(row.uid);
  }

  // actions
  loadPropertyDetails(uid: string) {
    this.getAttrProperties(uid);
  }

  addChild(parent: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyAddSimpleDialogComponent, {
        data: { parent, withAddressSearch: false },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
          this.appService.wasCreatedProperty$.next();
        }
      });
    }
  }

  clone(prop: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyCloneDialogComponent, {
        data: { prop },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
          this.appService.wasCreatedProperty$.next();
        }
      });
    }
  }

  transfertOwnership(prop: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyTransfertDialogComponent, {
        data: { prop },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  confirmDeleteProperty(prop: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            'Confirmez-vous la suppression du bien "' + prop.name + '" ?',
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeleteProperty(prop);
        }
      });
    }
  }

  doDeleteProperty(prop: RealProperty) {
    this.propertyService.deleteRealProperty(prop.uid).subscribe(() => {
      this.alertService.success("Suppression effectuée avec succès");
      this.loadData();
      this.appService.wasDeletedProperty$.next();
    });
  }

  togglePropertyArchive(prop: RealProperty) {
    this.actionService.togglePropertyArchive(prop);
  }

  toogleFavorite(prop: RealProperty) {
    this.subs.push(
      this.userService
        .toggleUserPreferencesFavorite(
          this.userHelper.currentUser.userId,
          prop.uid
        )
        .subscribe(() => this.loadData())
    );
  }

  // ---------------------------------------- //
  //                   APIS                   //
  // ---------------------------------------- //
  getDataRequestFilter(): DataRequestFilter {
    return {
      name: this.filterData?.name?.length > 1 ? this.filterData.name : null,
      types: this.filterData?.types || null,
      attributeCodes: this.filterData?.attributeCodes || null,
      attributeValues: this.filterData?.attributeValues || null,
      childrenType: this.filterData?.childrenType || this.defaultChildrenType,
    };
  }
  loadData() {
    this.isLoading = true;
    //this.dataSource = new MatTableDataSource([]);

    const dataFilter = this.getDataRequestFilter();

    if (this.archiveMode) {
      //load only Mine and shared property in archive mode
      this.subs.push(
        forkJoin([
          this.propertyService.getRealPropertyList(
            PropertyAccessTypeFilter.MINE,
            true,
            dataFilter.types,
            dataFilter.name,
            dataFilter.attributeCodes,
            dataFilter.attributeValues,
            this.propParentId,
            dataFilter.childrenType,
            this.sortFieldName,
            this.sortDirection,
            0,
            this.PAGING_ITEMS
          ),
          this.propertyService.getRealPropertyList(
            PropertyAccessTypeFilter.SHARED_NO_FAVORITE,
            true,
            dataFilter.types,
            dataFilter.name,
            dataFilter.attributeCodes,
            dataFilter.attributeValues,
            this.propParentId,
            dataFilter.childrenType,
            this.sortFieldName,
            this.sortDirection,
            0,
            this.PAGING_ITEMS
          ),
        ]).subscribe({
          next: ([myPagedList, sharedPagedList]) => {
            this.isLoading = false;

            this.myList = myPagedList.list;
            this.hasMoreMy =
              myPagedList.paging.totalItems > myPagedList.paging.curItems;
            this.countMoreMy =
              myPagedList.paging.totalItems - myPagedList.paging.curItems;

            this.sharedList = sharedPagedList.list;
            this.hasMoreShared =
              sharedPagedList.paging.totalItems >
              sharedPagedList.paging.curItems;
            this.countMoreShared =
              sharedPagedList.paging.totalItems -
              sharedPagedList.paging.curItems;

            this.afterGetLoadData();
          },
          error: () => {
            this.isLoading = false;
          },
        })
      );
    } else {
      // load favorites, mine and shared
      this.subs.push(
        forkJoin([
          this.propertyService.getRealPropertyList(
            PropertyAccessTypeFilter.FAVORITE,
            false,
            dataFilter.types,
            dataFilter.name,
            dataFilter.attributeCodes,
            dataFilter.attributeValues,
            this.propParentId,
            dataFilter.childrenType,
            this.sortFieldName,
            this.sortDirection,
            0,
            this.PAGING_ITEMS
          ),
          this.propertyService.getRealPropertyList(
            PropertyAccessTypeFilter.MINE_NO_FAVORITE,
            false,
            dataFilter.types,
            dataFilter.name,
            dataFilter.attributeCodes,
            dataFilter.attributeValues,
            this.propParentId,
            dataFilter.childrenType,
            this.sortFieldName,
            this.sortDirection,
            0,
            this.PAGING_ITEMS
          ),
          this.propertyService.getRealPropertyList(
            PropertyAccessTypeFilter.SHARED_NO_FAVORITE,
            false,
            dataFilter.types,
            dataFilter.name,
            dataFilter.attributeCodes,
            dataFilter.attributeValues,
            this.propParentId,
            dataFilter.childrenType,
            this.sortFieldName,
            this.sortDirection,
            0,
            this.PAGING_ITEMS
          ),
        ]).subscribe({
          next: ([favoritePagedList, myPagedList, sharedPagedList]) => {
            this.isLoading = false;
            this.favoriteList = favoritePagedList.list;
            this.hasMoreFavorite =
              favoritePagedList.paging.totalItems >
              favoritePagedList.paging.curItems;
            this.countMoreFavorite =
              favoritePagedList.paging.totalItems -
              favoritePagedList.paging.curItems;

            this.myList = myPagedList.list;
            this.hasMoreMy =
              myPagedList.paging.totalItems > myPagedList.paging.curItems;
            this.countMoreMy =
              myPagedList.paging.totalItems - myPagedList.paging.curItems;

            this.sharedList = sharedPagedList.list;
            this.hasMoreShared =
              sharedPagedList.paging.totalItems >
              sharedPagedList.paging.curItems;
            this.countMoreShared =
              sharedPagedList.paging.totalItems -
              sharedPagedList.paging.curItems;
            this.afterGetLoadData();
          },
          error: () => {
            this.isLoading = false;
          },
        })
      );
    }
  }

  loadMoreFavorite() {
    this.loadingMoreFavorite = true;
    const dataFilter = this.getDataRequestFilter();

    this.subs.push(
      this.propertyService
        .getRealPropertyList(
          PropertyAccessTypeFilter.FAVORITE,
          false,
          dataFilter.types,
          dataFilter.name,
          dataFilter.attributeCodes,
          dataFilter.attributeValues,
          this.propParentId,
          dataFilter.childrenType,
          this.sortFieldName,
          this.sortDirection,
          this.favoriteList.length,
          this.PAGING_ITEMS
        )
        .subscribe({
          next: (data) => {
            this.loadingMoreFavorite = false;
            this.favoriteList = this.favoriteList.concat(data.list);
            this.hasMoreFavorite =
              data.paging.totalItems > this.favoriteList.length;
            this.countMoreFavorite =
              data.paging.totalItems - this.favoriteList.length;
            this.afterGetLoadData();
          },
          error: () => {
            this.loadingMoreFavorite = false;
          },
        })
    );
  }

  loadMoreMy() {
    this.loadingMoreMy = true;

    const dataFilter = this.getDataRequestFilter();

    this.subs.push(
      this.propertyService
        .getRealPropertyList(
          this.archiveMode
            ? PropertyAccessTypeFilter.MINE
            : PropertyAccessTypeFilter.MINE_NO_FAVORITE,
          this.archiveMode,
          dataFilter.types,
          dataFilter.name,
          dataFilter.attributeCodes,
          dataFilter.attributeValues,
          this.propParentId,
          dataFilter.childrenType,
          this.sortFieldName,
          this.sortDirection,
          this.myList.length,
          this.PAGING_ITEMS
        )
        .subscribe({
          next: (data) => {
            this.loadingMoreMy = false;
            this.myList = this.myList.concat(data.list);
            this.hasMoreMy = data.paging.totalItems > this.myList.length;
            this.countMoreMy = data.paging.totalItems - this.myList.length;
            this.afterGetLoadData();
          },
          error: () => {
            this.loadingMoreMy = false;
          },
        })
    );
  }

  loadMoreShared() {
    this.loadingMoreShared = true;

    const dataFilter = this.getDataRequestFilter();

    this.subs.push(
      this.propertyService
        .getRealPropertyList(
          PropertyAccessTypeFilter.SHARED_NO_FAVORITE,
          this.archiveMode,
          dataFilter.types,
          dataFilter.name,
          dataFilter.attributeCodes,
          dataFilter.attributeValues,
          this.propParentId,
          dataFilter.childrenType,
          this.sortFieldName,
          this.sortDirection,
          this.sharedList.length,
          this.PAGING_ITEMS
        )
        .subscribe({
          next: (data) => {
            this.loadingMoreShared = false;
            this.sharedList = this.sharedList.concat(data.list);
            this.hasMoreShared =
              data.paging.totalItems > this.sharedList.length;
            this.countMoreShared =
              data.paging.totalItems - this.sharedList.length;
            this.afterGetLoadData();
          },
          error: () => {
            this.loadingMoreShared = false;
          },
        })
    );
  }
  getAttrProperties(propUid: string) {
    this.subs.push(
      this.propertyService.getRealPropertyAttributesSummary(propUid).subscribe({
        next: (propAttrs: PropertyAttribute[]) => {
          this.afterGetPropAttr(propUid, propAttrs);
        },
      })
    );
  }

  certify(prop: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyCertifyDialogComponent, {
        // width: '20rem'
        data: { prop },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  addShare(prop: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(ShareDialogComponent, {
        // width: '20rem'
        data: { props: [prop] },
      });
    }
  }

  getSeparatorRow(
    type: "favoriteProperty" | "myProperty" | "sharedProperty"
  ): IRealProperty {
    const row: IRealProperty = {
      name: "",
      type: "PUBLIC",
      owner: {
        firstName: "",
        name: "",
        uid: "",
      },
      isRow: true,
    };
    if (type === "favoriteProperty") {
      row.name = "Mes favoris";
    } else if (type === "myProperty") {
      row.name = this.archiveMode
        ? "Mes propriétés archivées"
        : "Mes propriétés";
    } else {
      row.name = "Propriétés partagées";
    }

    return row;
  }

  getMoreRow(
    type: "favoriteProperty" | "myProperty" | "sharedProperty"
  ): IRealProperty {
    const row: IRealProperty = {
      name: "",
      type: "PUBLIC",
      owner: {
        firstName: "",
        name: "",
        uid: "",
      },
      isRow: true,
    };
    if (type === "favoriteProperty") {
      row.name = `Voir les ${this.countMoreFavorite} biens suivant`;
      row.rowActionId = "favoriteProperty";
      row.rowLoading = this.loadingMoreFavorite;
    } else if (type === "myProperty") {
      row.name = `Voir les ${this.countMoreMy} biens suivant`;
      row.rowActionId = "myProperty";
      row.rowLoading = this.loadingMoreMy;
    } else {
      row.name = `Voir les ${this.countMoreShared} biens suivant`;
      row.rowActionId = "sharedProperty";
      row.rowLoading = this.loadingMoreShared;
    }

    return row;
  }

  triggerRowAction(
    actionId: "favoriteProperty" | "myProperty" | "sharedProperty"
  ) {
    if (actionId === "favoriteProperty") {
      this.loadMoreFavorite();
    } else if (actionId === "myProperty") {
      this.loadMoreMy();
    } else {
      this.loadMoreShared();
    }
  }

  onExpandedRow(item: IRealProperty) {
    item.alreadyBeenLoaded = true;
    return "expanded";
  }
}
